'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { toast } from 'sonner';
import { Building, CheckCircle, Filter, Mail, Search, Shield, User, XCircle } from 'lucide-react';
import Link from 'next/link';

interface MeetingUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  department?: { id: string; name: string; code?: string | null } | null;
  position?: { id: string; name: string; level?: number | null } | null;
  isActive: boolean;
  createdAt: string;
  createdMeetingsCount?: number;
  attendancesCount?: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const LEGACY_ROLE_MAP: Record<string, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'MeetingManager',
  MEETINGMANAGER: 'MeetingManager',
  LEADER: 'Leader',
  EMPLOYEE: 'Employee',
};

const normalizeRoleValue = (role?: string) => {
  const trimmed = role?.trim();
  if (!trimmed) return '';
  const upperRole = trimmed.toUpperCase();
  return LEGACY_ROLE_MAP[upperRole] || trimmed;
};

export default function MeetingAttendanceUsersPage() {
  const { isReady } = useAuthGuard();
  const { t } = useTranslation();

  const [users, setUsers] = useState<MeetingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [departments, setDepartments] = useState<string[]>([]);
  const [filters, setFilters] = useState({ search: '', role: '', department: '' });
  const organizationMembersUrl = 'http://localhost:3000/organization/members';

  const updatePagination = useCallback((next?: Pagination) => {
    if (!next) {
      return;
    }
    setPagination((prev) => {
      if (
        prev.page === next.page &&
        prev.limit === next.limit &&
        prev.total === next.total &&
        prev.totalPages === next.totalPages
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await meetingAttendanceApi.listUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        role: normalizeRoleValue(filters.role) || undefined,
        department: filters.department || undefined,
      });
      setUsers(data.users || []);
      updatePagination(data.pagination);
      setDepartments(data.departments || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error(t.meetingAttendance.users.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [filters.department, filters.role, filters.search, pagination.limit, pagination.page, t.meetingAttendance.users.loadFailed, updatePagination]);

  useEffect(() => {
    if (isReady) {
      fetchUsers();
    }
  }, [fetchUsers, isReady]);

  const roleLabel = useCallback(
    (role: string) => {
      const normalizedRole = normalizeRoleValue(role);
      if (normalizedRole === 'Administrator') return t.meetingAttendance.users.roleDisplay.admin;
      if (normalizedRole === 'MeetingManager') return t.meetingAttendance.users.roleDisplay.manager;
      if (normalizedRole === 'Leader') return t.meetingAttendance.users.roleDisplay.leader;
      if (normalizedRole === 'Employee') return t.meetingAttendance.users.roleDisplay.employee;
      return role;
    },
    [t],
  );

  const roleFilterOptions = useMemo(
    () => ([
      { value: '', label: t.meetingAttendance.users.allRoles },
      { value: 'Administrator', label: t.meetingAttendance.users.roleDisplay.admin },
      { value: 'MeetingManager', label: t.meetingAttendance.users.roleDisplay.manager },
      { value: 'Leader', label: t.meetingAttendance.users.roleDisplay.leader },
      { value: 'Employee', label: t.meetingAttendance.users.roleDisplay.employee },
    ]),
    [t],
  );

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">{t.meetingAttendance.common.loading}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.meetingAttendance.users.title}</h1>
          <p className="text-gray-600">{t.meetingAttendance.users.subtitle}</p>
        </div>
        <Link
          href={organizationMembersUrl}
          target="_blank"
          rel="noreferrer"
          className={cn(buttonVariants({ variant: 'outline' }))}
        >
          {t.meetingAttendance.users.openOrgMembers}
        </Link>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>{t.meetingAttendance.users.searchLabel}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  value={filters.search}
                  onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                  placeholder={t.meetingAttendance.users.searchPlaceholder}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>{t.meetingAttendance.users.roleFilterLabel}</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={filters.role}
                onChange={(event) => setFilters({ ...filters, role: event.target.value })}
              >
                {roleFilterOptions.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t.meetingAttendance.users.departmentFilterLabel}</Label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={filters.department}
                onChange={(event) => setFilters({ ...filters, department: event.target.value })}
              >
                <option value="">{t.meetingAttendance.users.allDepartments}</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={() => fetchUsers()} className="w-full">
                <Filter className="mr-2 h-4 w-4" />
                {t.meetingAttendance.users.applyFilters}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-gray-500">{t.meetingAttendance.common.loading}</div>
      ) : users.length === 0 ? (
        <div className="text-sm text-gray-500">{t.meetingAttendance.common.empty}</div>
      ) : (
        <div className="space-y-4">
          {users.map((row) => {
            const createdCount = row.createdMeetingsCount ?? 0;
            const attendanceCount = row.attendancesCount ?? 0;
            const registrationDate = row.createdAt
              ? new Date(row.createdAt).toLocaleDateString('en-US')
              : t.meetingAttendance.common.notAvailable;
            const companyDepartment = row.department?.name
              ? `${row.department.name}${row.position?.name ? ` - ${row.position.name}` : ''}`
              : '';
            return (
              <Card key={row.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-medium">{row.displayName}</h3>
                          {row.isActive ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="mr-2 h-4 w-4" />
                            {row.email}
                          </div>
                          {companyDepartment && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Building className="mr-2 h-4 w-4" />
                              {companyDepartment}
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-600">
                            <Shield className="mr-2 h-4 w-4" />
                            {roleLabel(row.role)}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span>{t.meetingAttendance.users.createdMeetingsLabel}: {createdCount}</span>
                          <span>{t.meetingAttendance.users.attendanceRecordsLabel}: {attendanceCount}</span>
                          <span>{t.meetingAttendance.users.registrationDateLabel}: {registrationDate}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between pt-4">
        <span className="text-sm text-muted-foreground">
          {t.meetingAttendance.users.pageLabel
            .replace('{start}', String((pagination.page - 1) * pagination.limit + 1))
            .replace('{end}', String(Math.min(pagination.page * pagination.limit, pagination.total)))
            .replace('{total}', String(pagination.total))}
        </span>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination({ ...pagination, page: Math.max(1, pagination.page - 1) })}
            disabled={pagination.page <= 1}
          >
            {t.meetingAttendance.users.prevPage}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination({ ...pagination, page: Math.min(pagination.totalPages, pagination.page + 1) })}
            disabled={pagination.page >= pagination.totalPages}
          >
            {t.meetingAttendance.users.nextPage}
          </Button>
        </div>
      </div>

    </div>
  );
}
